import dayjs from 'dayjs';

import i18n from '@/core/i18n';
import { DATE_KEY_FORMAT } from '@/helpers';
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { getProjectStatusText } from '@/modules/common/components/molecules/badge/status-configs/project-status-config';
import { mapProjectStatusFromDTO } from '@/modules/projects/mappers/project-status.mapper';
import { StringUtils } from '@/utils/string/string-utils';

import { AssignmentType, RowItem, RowItemType, TimeOffBlockType } from '../types';
import { PlannerProjectResponse } from '../types/dto';
import { mapAssignmentsFromPlannerResponse } from './assignment.mapper';
import { mapTimeOffsFromPlannerResponse } from './time-off.mapper';

/**
 * Adapter to convert PlannerProjectResponse[] from API to the format
 * expected by the planner UI components.
 *
 * Transforms API response structure to match the planner UI requirements.
 *
 * @param loadedDateRange - The full date range that has been fetched. Dates within
 *   this range that have no group-level utilization data are marked as noDataDates
 *   so the UI can show "-" instead of a misleading 0%.
 */
export const adaptPlannerProjectsToProjectData = (
  projects: PlannerProjectResponse[] | undefined,
  loadedDateRange?: { startDate: string; endDate: string } | null
): {
  rows: RowItem[];
  assignments: Map<string, AssignmentType[]>;
  timeOffs: Map<string, TimeOffBlockType[]>;
} => {
  if (!projects || projects.length === 0) {
    return { rows: [], assignments: new Map(), timeOffs: new Map() };
  }

  const result = projects.reduce(
    (acc, projectResponse) => {
      // Skip items with null project (backend may return unassigned members without a project)
      if (!projectResponse.project) return acc;

      const projectId = String(projectResponse.project.id);
      projectResponse.project.name =
        StringUtils.nullIfEmpty(projectResponse.project.name) ?? i18n.t('common:noNameProject');
      const projectName = projectResponse.status
        ? `${projectResponse.project.name} (${getProjectStatusText(mapProjectStatusFromDTO(projectResponse.status))})`
        : projectResponse.project.name;
      const projectColor = projectResponse.project.colorHex
        ? '#' + projectResponse.project.colorHex
        : DEFAULT_COLOR_PALETTE.slate;

      // Convert utilization array to map for project (extract date part as key)
      // Fix percent when backend returns 0% for groups with 0 capacity but assigned work
      const projectUtilizationMap = new Map(
        projectResponse.utilizationByDate.map(util => [
          dayjs(util.date).format(DATE_KEY_FORMAT),
          util.capacity === 0 ? { ...util, percent: 100 } : util,
        ])
      );

      // Detect dates where group-level utilization is unavailable. Two cases:
      // 1. Cross-group backfill: a member was copied from another group — the member
      //    has data but the group-level aggregation doesn't.
      // 2. Empty horizontal fetch: the backend returned no items for a date range
      //    (e.g. items: []) — dates in that range have no utilization for either
      //    the group or its members. We detect these by iterating the loaded date
      //    range and flagging any weekday not present in group-level utilization.
      const groupUtilDates = new Set(
        projectResponse.utilizationByDate.map(u => dayjs(u.date).format(DATE_KEY_FORMAT))
      );
      const noDataDates = new Set<string>();

      // Case 1: member has utilization for a date the group doesn't
      projectResponse.members.forEach(member => {
        member.utilizationByDate.forEach(u => {
          const dateKey = dayjs(u.date).format(DATE_KEY_FORMAT);
          if (!groupUtilDates.has(dateKey)) {
            noDataDates.add(dateKey);
          }
        });
      });

      // Case 2: loaded date range has weekdays with no group utilization at all
      if (loadedDateRange) {
        let current = dayjs(loadedDateRange.startDate);
        const end = dayjs(loadedDateRange.endDate);
        while (!current.isAfter(end)) {
          const isWeekend = current.day() === 0 || current.day() === 6;
          if (!isWeekend) {
            const dateKey = current.format(DATE_KEY_FORMAT);
            if (!groupUtilDates.has(dateKey)) {
              noDataDates.add(dateKey);
            }
          }
          current = current.add(1, 'day');
        }
      }

      // Add project group row
      acc.rows.push({
        id: projectId,
        title: projectName,
        color: projectColor,
        hoursPerDay: 8,
        type: RowItemType.Group,
        utilizationByDate: projectUtilizationMap,
        ...(noDataDates.size > 0 && { noDataDates }),
      });

      // Process each member in the project
      projectResponse.members.forEach(member => {
        const memberId = `${projectId}-${member.user.id}`;
        const currentState = member.stateChanges[0];
        const memberHoursPerDay = currentState?.workingMinutesPerDay
          ? currentState.workingMinutesPerDay / 60
          : 8;

        // Convert utilization array to map for member (extract date part as key)
        // Fix percent when backend returns 0% for members with 0 capacity but assigned work
        const memberUtilizationMap = new Map(
          member.utilizationByDate.map(util => [
            dayjs(util.date).format(DATE_KEY_FORMAT),
            util.capacity === 0 ? { ...util, percent: 100 } : util,
          ])
        );

        // Add user row under the project
        acc.rows.push({
          id: memberId,
          title: member.user.name,
          hoursPerDay: memberHoursPerDay,
          type: RowItemType.User,
          user: member.user,
          stateChanges: member.stateChanges,
          utilizationByDate: memberUtilizationMap,
          parentGroupId: projectId,
        });

        // Map assignments for this user within this project
        const mappedAssignments = mapAssignmentsFromPlannerResponse(member.assignments);
        acc.assignments.set(memberId, mappedAssignments);

        // Map time-offs for this user
        const mappedTimeOffs = mapTimeOffsFromPlannerResponse(member.timeOffs);
        acc.timeOffs.set(memberId, mappedTimeOffs);
      });

      return acc;
    },
    { rows: [], assignments: new Map(), timeOffs: new Map() } as {
      rows: RowItem[];
      assignments: Map<string, AssignmentType[]>;
      timeOffs: Map<string, TimeOffBlockType[]>;
    }
  );

  // Sort user rows alphabetically WITHIN their parent group.
  // Build the sorted list by iterating groups in order and sorting each group's members.
  const groupIndices: number[] = [];
  result.rows.forEach((row, i) => {
    if (row.type === RowItemType.Group) groupIndices.push(i);
  });

  const sortedRows: RowItem[] = [];
  groupIndices.forEach((groupIdx, gi) => {
    sortedRows.push(result.rows[groupIdx]);
    const nextGroupIdx = gi + 1 < groupIndices.length ? groupIndices[gi + 1] : result.rows.length;
    const members = result.rows.slice(groupIdx + 1, nextGroupIdx);
    members.sort((a, b) =>
      a.title.trim().toLocaleLowerCase().localeCompare(b.title.trim().toLocaleLowerCase())
    );
    sortedRows.push(...members);
  });
  result.rows = sortedRows;

  return result;
};
